import { Box, Typography } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import LoaderComponent from 'components/Loader';
import { useEffect, useRef } from 'react';
import { EChartsOption } from 'echarts';
import { convertMillionsToAbsolute } from 'utils/finopsDataAdapter';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import { useSearchContext } from 'Context/contexts/searchContext';

interface MonthlyItemData {
	month: number;
	name: string;
	actualCost: number;
	forecastCost: number | null;
}

interface FinOpsMonthlyTrendChartProps {
	isLoading?: boolean;
	monthlyTotals: any[];
	monthlyItemsData?: MonthlyItemData[];
	monthlyAopAverage?: number;
	onInsightsClick?: (month: string) => void;
}

// Helper function to generate HTML with highlighting
const getHighlightedHTML = (text: string, searchTerm: string): string => {
	if (!searchTerm) return text;

	const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	const escapedSearchTerm = escapeRegex(searchTerm);
	const regex = new RegExp(`(${escapedSearchTerm})`, "gi");

	return text.replace(regex, '<span style="background-color: yellow; padding: 0 2px; border-radius: 3px;">$1</span>');
};

const getFinOpsChartOptions = (
	monthlyTotals: any[],
	monthlyItemsData: MonthlyItemData[] = [],
	monthlyAopAverage: number,
	searchTerm: string = '',
	onInsightsClick?: (month: string) => void
): EChartsOption => {
	if (!monthlyTotals || monthlyTotals.length === 0) {
		console.warn('[FinOpsChart] No monthlyTotals data provided');
		return {};
	}

	if (!Array.isArray(monthlyTotals)) {
		console.error('[FinOpsChart] monthlyTotals is not an array:', typeof monthlyTotals);
		return {};
	}

	const validMonthlyTotals = monthlyTotals.filter(m => {
		if (!m || typeof m !== 'object') {
			console.warn('[FinOpsChart] Invalid monthly data item:', m);
			return false;
		}
		if (!m.month || typeof m.month !== 'number') {
			console.warn('[FinOpsChart] Missing or invalid month property:', m);
			return false;
		}
		return true;
	});

	if (validMonthlyTotals.length === 0) {
		console.error('[FinOpsChart] No valid monthly data after filtering');
		return {};
	}

	const months = validMonthlyTotals.map((m, index) => {
		const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
		const year = m.year || new Date().getFullYear();
		return `${monthNames[m.month - 1]} ${year}`;
	});

	// Calculate actual spend by summing all items for each month
	const actualSpendData = validMonthlyTotals.map(m => {
		const monthItems = monthlyItemsData.filter(p => p.month === m.month);
		const totalForMonth = monthItems.reduce((sum, p) => sum + (p.actualCost || 0), 0);
		return convertMillionsToAbsolute(totalForMonth);
	});

	// Calculate forecast by summing all items for each month
	const forecastData = validMonthlyTotals.map(m => {
		const monthItems = monthlyItemsData.filter(p => p.month === m.month);
		const totalForMonth = monthItems.reduce((sum, p) => sum + (p.forecastCost || 0), 0);
		return convertMillionsToAbsolute(totalForMonth);
	});

	const aopLineData = validMonthlyTotals.map(() => convertMillionsToAbsolute(monthlyAopAverage));

	const itemTotals = monthlyItemsData.reduce((acc, item) => {
		if (!acc[item.name]) {
			acc[item.name] = 0;
		}
		acc[item.name] += item.actualCost || 0;
		return acc;
	}, {} as Record<string, number>);

	const sortedItems = Object.entries(itemTotals)
		.sort(([nameA], [nameB]) => nameA.localeCompare(nameB));

	const itemColors = [
		'#00C3F8', // Cyan (Azure)
		'#00E676', // Green (GCP)
		'#FFA726', // Orange (AWS)
		'#FF7043', // Coral (Databricks)
		'#FF2A85', // Magenta (Teradata)
		'#AB47BC', // Purple
		'#29B6F6', // Sky Blue
		'#26A69A'  // Teal
	];

	const itemSeries = sortedItems.map(([itemName], index) => {
		const itemData = validMonthlyTotals.map(m => {
			const itemMonth = monthlyItemsData.find(
				p => p.name === itemName && p.month === m.month
			);
			return convertMillionsToAbsolute(itemMonth?.actualCost || 0);
		});

		return {
			name: itemName.toUpperCase(),
			type: 'bar' as const,
			stack: 'items',
			itemStyle: { color: itemColors[index % itemColors.length] },
			emphasis: {
				focus: 'series' as const
			},
			data: itemData,
			z: 2
		};
	});

	const today = new Date();
	const currentMonth = today.getMonth() + 1;
	const currentMonthIndex = validMonthlyTotals.findIndex(m => m.month === currentMonth);

	return {
		tooltip: {
			trigger: 'axis',
			backgroundColor: '#FFFFFF',
			borderColor: '#009BD8',
			borderWidth: 1,
			extraCssText: 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);',
			textStyle: { color: '#333333', fontSize: 12, fontFamily: 'Montserrat' },
			axisPointer: {
				type: 'shadow'
			},
			formatter: function (params: any) {
				let tooltipContent = `<strong>${params[0].axisValue}</strong><br>`;
				params.forEach((param: any) => {
					if (param.value && param.value !== 0) {
						const value = `$${(param.value / 1000000).toFixed(2)}MM`;
						tooltipContent += `${param.marker} ${param.seriesName}: ${value}<br>`;
					}
				});
				return tooltipContent;
			}
		},
		legend: {
			type: 'scroll',
			bottom: '5',
			left: 'center',
			orient: 'horizontal',
			textStyle: { color: '#555555', fontSize: 11, fontFamily: 'Montserrat' },
			pageIconColor: '#555555',
			pageIconInactiveColor: '#CCCCCC',
			pageTextStyle: { color: '#555555', fontSize: 10 },
			itemGap: 15,
			data: ['Planned AOP', 'Forecast', ...sortedItems.map(([name]) => name.toUpperCase())]
		},
		grid: {
			left: '6%',
			right: '4%',
			bottom: '18%',
			top: '40px',
			containLabel: true
		},
		xAxis: {
			type: 'category',
			data: months,
			axisLabel: {
				color: '#555555',
				fontSize: 11,
				rotate: 0,
				interval: 0,
				fontFamily: 'Montserrat',
				formatter: (value: string) => {
					return getHighlightedHTML(value, searchTerm);
				}
			},
			axisTick: { show: false },
			axisLine: { lineStyle: { color: '#D0D0D0' } }
		},
		yAxis: {
			type: 'value',
			name: 'Value in Million Dollars',
			nameLocation: 'middle',
			nameGap: 35,
			nameRotate: 90,
			nameTextStyle: {
				color: '#71758F',
				fontSize: 11,
				fontFamily: 'Montserrat'
			},
			axisLabel: {
				color: '#71758F',
				fontSize: 11,
				fontFamily: 'Montserrat',
				formatter: (value: number) => {
					if (value === 0) return '0';
					return `${(value / 1000000).toFixed(1)}`;
				}
			},
			splitLine: {
				show: true,
				lineStyle: { color: '#E0E0E0', type: 'dotted', opacity: 0.8 }
			},
			axisLine: { show: false },
			max: function (value: any) {				
				const maxWithMargin = value.max * 1.2; // 20% margin				
				return Math.ceil(maxWithMargin / 3000000) * 3000000;
			}
		},
		series: [
			{
				name: 'Planned AOP',
				type: 'line',
				smooth: true,
				showSymbol: false,
				lineStyle: { color: '#009BD8', width: 2.5 },
				itemStyle: { color: '#009BD8' },
				data: actualSpendData,
				z: 6,
				markPoint: {
					symbol: 'circle',
					symbolSize: 22,
					silent: false,
					itemStyle: {
						color: '#0A3A60',
						borderColor: '#0A3A60',
						borderWidth: 1,
						shadowColor: 'rgba(0, 0, 0, 0.25)',
						shadowBlur: 4,
						shadowOffsetY: 2
					},
					label: {
						show: true,
						formatter: '!',
						color: '#FFFFFF',
						fontSize: 14,
						fontWeight: 800,
						fontFamily: 'Montserrat'
					},
					tooltip: {
						show: false
					},
					data: validMonthlyTotals
						.map((m, index) => {
							const monthItems = monthlyItemsData.filter(p => p.month === m.month);
							const totalForMonth = monthItems.reduce((sum, p) => sum + (p.actualCost || 0), 0);
							const actualVal = convertMillionsToAbsolute(totalForMonth);
							if (actualVal > 0) {
								return { name: '', xAxis: index, yAxis: actualVal };
							}
							return null;
						})
						.filter((d): d is { name: string; xAxis: number; yAxis: number } => d !== null)
				},
				...(currentMonthIndex >= 0 && {
					markLine: {
						symbol: 'none',
						label: {
							show: true,
							position: 'end',
							formatter: 'Today',
							color: '#71758F',
							fontFamily: 'Montserrat',
							fontWeight: 500,
							fontSize: 12,
							distance: 5
						},
						lineStyle: {
							color: '#A9ADCB',
							type: 'dotted',
							width: 1
						},
						data: [{ xAxis: currentMonthIndex }]
					}
				})
			},
			{
				name: 'Forecast',
				type: 'line',
				smooth: false,
				showSymbol: false,
				lineStyle: { color: '#388E3C', width: 2 },
				itemStyle: { color: '#388E3C' },
				data: aopLineData,
				z: 5
			},
			...itemSeries
		]
	};
};

const FinOpsMonthlyTrendChart = ({
	isLoading = false,
	monthlyTotals = [],
	monthlyItemsData = [],
	monthlyAopAverage = 0,
	onInsightsClick
}: FinOpsMonthlyTrendChartProps) => {
	const { searchTerm } = useSearchContext();
	const chartRef = useRef<ReactECharts>(null);
	const chartOption = getFinOpsChartOptions(monthlyTotals, monthlyItemsData, monthlyAopAverage, searchTerm, onInsightsClick);

	useEffect(() => {
		const handleResize = () => {
			if (chartRef.current) {
				const chartInstance = chartRef.current.getEchartsInstance();
				if (chartInstance && !chartInstance.isDisposed()) {
					chartInstance.resize();
				}
			}
		};

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
			if (chartRef.current) {
				try {
					const chartInstance = chartRef.current.getEchartsInstance();
					if (chartInstance && !chartInstance.isDisposed()) {
						chartInstance.dispose();
					}
				} catch (error) {
					console.warn('[FinOpsChart] Error disposing chart:', error);
				}
			}
		};
	}, []);

	const onChartClick = (params: any) => {
		if (params.componentType === 'markPoint' && onInsightsClick) {
			const monthIndex = params.data?.xAxis;
			if (monthIndex !== undefined && monthlyTotals[monthIndex]) {
				const clickedMonth = monthlyTotals[monthIndex];
				const year = clickedMonth.year || new Date().getFullYear();
				const monthStr = `${year}-${String(clickedMonth.month).padStart(2, '0')}`;
				onInsightsClick(monthStr);
			} else {
				const fallbackMonth = new Date().toISOString().slice(0, 7);
				onInsightsClick(fallbackMonth);
			}
		}
	};

	const renderChartContent = () => {
		if (isLoading) {
			return <LoaderComponent />;
		}

		if (monthlyTotals.length > 0) {
			return (
				<ReactECharts
					ref={chartRef}
					option={chartOption}
					style={{ width: '100%', height: '100%' }}
					opts={{ renderer: 'canvas' }}
					notMerge={false}
					lazyUpdate={false}
					onEvents={{
						click: onChartClick
					}}
				/>
			);
		}

		return (
			<Typography color="text.secondary" variant="bodyText">
				No data available
			</Typography>
		);
	};

	return (
		<Box
			bgcolor="#FBFDFF"
			borderRadius="8px"
			sx={{
				height: '100%',
				minHeight: '268px',
				width: '100%',
				display: 'flex',
				flexDirection: 'column',
				boxSizing: 'border-box',
				p: 2,
				boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
			}}
		>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
				<Typography
					variant="widgetTitle"
					color="#000000"
					sx={{ fontWeight: 600, fontSize: '18px', fontFamily: 'Montserrat' }}
				>
					<HighlightedText text="Spend VS Plan & Forecast (Monthly)" />
				</Typography>
			</Box>

			<Box
				sx={{
					flexGrow: 1,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				{renderChartContent()}
			</Box>
		</Box>
	);
};

export default FinOpsMonthlyTrendChart;
